import { ReactElement, useState } from 'react';
import {
    UIFormInput,
    UIInlineAlert,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText,
    UITextButton
} from '@/ui';
import { useBulkUpdateDeviceFallAlertAction } from '../actions';
import { useBulkAlertLicenses } from '../queries';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

const DEFAULT_SENSITIVITY = '4';

export type BulkFallModalProps = {
    companyId: string;
    deviceIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onApplied: () => void;
};

export function BulkFallModal(props: BulkFallModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: BulkFallModalProps): ReactElement {
    const { companyId, deviceIds, onClose, onApplied } = props;
    const { t } = useDevicesTranslation();
    const { bulkUpdateDeviceFallAlert, loading } =
        useBulkUpdateDeviceFallAlertAction({ companyId });
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });

    const [enabled, setEnabled] = useState(true);
    const [sensitivity, setSensitivity] = useState(DEFAULT_SENSITIVITY);
    const [showErrors, setShowErrors] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    function handleResetDefaults() {
        setSensitivity(DEFAULT_SENSITIVITY);
        setShowErrors(false);
    }

    function sensitivityError(): string {
        const value = Number(sensitivity);
        if (!Number.isInteger(value) || value < 1 || value > 9) {
            return t('shared.fields.fall_sensitivity.error_invalid');
        }
        return '';
    }

    // Enabling on more devices than there are free licences is blocked. Until
    // usage has loaded we don't know the free count, so apply is disabled.
    const blocked = enabled && licenses.fall.toEnable > licenses.fall.free;

    function onApply() {
        setShowErrors(true);
        if (enabled && sensitivityError()) {
            setShowAdvanced(true);
            return;
        }
        if (blocked) {
            return;
        }
        bulkUpdateDeviceFallAlert(
            enabled
                ? {
                      deviceIds,
                      enabled: true,
                      sensitivity: Number(sensitivity)
                  }
                : { deviceIds, enabled: false }
        )
            .then(() => onApplied())
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('components.bulk_fall_modal.title')}
                subtitle={
                    deviceIds.length > 1
                        ? t('shared.devices.bulk_applies_to', {
                              count: deviceIds.length
                          })
                        : undefined
                }
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <PendingConfigNote />

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <UIText>{t('shared.devices.fall_label')}</UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <div style={{ alignSelf: 'flex-start' }}>
                                <UITextButton
                                    type="button"
                                    variant="primary"
                                    leftIcon={
                                        showAdvanced
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    }
                                    onClick={() =>
                                        setShowAdvanced((show) => !show)
                                    }
                                >
                                    {t('shared.labels.advanced_settings')}
                                </UITextButton>
                            </div>
                            {showAdvanced && (
                                <UIFormInput
                                    label={t(
                                        'shared.devices.fall_sensitivity_label'
                                    )}
                                    description={t(
                                        'shared.devices.fall_sensitivity_description'
                                    )}
                                    type="number"
                                    value={sensitivity}
                                    onChange={setSensitivity}
                                    errorText={
                                        showErrors ? sensitivityError() : ''
                                    }
                                />
                            )}
                        </>
                    )}

                    {!licenses.loading && blocked && (
                        <UIInlineAlert
                            text={t(
                                'components.bulk_fall_modal.not_enough_licences',
                                {
                                    count:
                                        licenses.fall.toEnable -
                                        licenses.fall.free
                                }
                            )}
                        />
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.apply'),
                        loading: loading,
                        disabled: loading || blocked || licenses.loading,
                        onClick: onApply
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    },
                    ...(enabled && showAdvanced
                        ? [
                              {
                                  variant: 'textDefault' as const,
                                  type: 'button' as const,
                                  text: t('shared.labels.revert_default'),
                                  disabled: loading,
                                  onClick: handleResetDefaults
                              }
                          ]
                        : [])
                ]}
            />
        </>
    );
}
